import { Sparkles, Stars } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import { AdditiveBlending, Color, type Group, type Points } from "three";

export function SpaceBackdrop({ lite = false }: { lite?: boolean }) {
  const nebula = useRef<Group>(null);
  const dust = useRef<Points>(null);
  const dustPositions = useMemo(() => {
    const n = lite ? 220 : 900;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 90;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 50;
      arr[i * 3 + 2] = -8 - Math.random() * 70;
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (nebula.current) {
      nebula.current.rotation.y = t * 0.012;
      nebula.current.rotation.z = Math.sin(t * 0.07) * 0.04;
    }
    if (dust.current) {
      dust.current.rotation.y = t * 0.008;
      dust.current.rotation.x = Math.sin(t * 0.03) * 0.02;
    }
  });

  return (
    <>
      <color attach="background" args={["#02040a"]} />
      {/* FogExp2 is driven by DepthFog from the active generation */}
      <Stars radius={140} depth={90} count={lite ? 1800 : 7000} factor={lite ? 2.4 : 3.6} saturation={0.12} fade speed={0.08} />
      {!lite && <Stars radius={80} depth={40} count={1800} factor={1.6} saturation={0.2} fade speed={0.04} />}
      {!lite && <Sparkles count={80} scale={[40, 22, 30]} size={3} speed={0.35} opacity={0.45} color="#9ee7ff" />}
      <group ref={nebula}>
        <mesh position={[-38, 16, -70]} scale={[34, 14, 10]}>
          <sphereGeometry args={[1, 24, 18]} />
          <meshBasicMaterial color="#0c3a48" transparent opacity={0.07} depthWrite={false} blending={AdditiveBlending} />
        </mesh>
        <mesh position={[42, -18, -78]} scale={[36, 20, 12]}>
          <sphereGeometry args={[1, 24, 18]} />
          <meshBasicMaterial color="#3a1450" transparent opacity={0.06} depthWrite={false} blending={AdditiveBlending} />
        </mesh>
        <mesh position={[6, 10, -88]} scale={[48, 10, 14]}>
          <sphereGeometry args={[1, 24, 18]} />
          <meshBasicMaterial color="#102040" transparent opacity={0.05} depthWrite={false} blending={AdditiveBlending} />
        </mesh>
      </group>
      <points ref={dust}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[dustPositions, 3]} />
        </bufferGeometry>
        <pointsMaterial
          size={0.06}
          color={new Color("#8fd0ff")}
          transparent
          opacity={0.35}
          depthWrite={false}
          blending={AdditiveBlending}
          sizeAttenuation
        />
      </points>
      <mesh position={[-2.2, 9.5, -34]}>
        <sphereGeometry args={[1.05, 48, 48]} />
        <meshBasicMaterial color="#fff6d8" />
      </mesh>
      <mesh position={[-2.2, 9.5, -34]} scale={2.4}>
        <sphereGeometry args={[1.05, 24, 24]} />
        <meshBasicMaterial color="#ffd089" transparent opacity={0.12} depthWrite={false} blending={AdditiveBlending} />
      </mesh>
      <pointLight position={[-2.2, 9.5, -34]} color="#fff0d1" intensity={420} distance={120} decay={1.55} />
      <pointLight position={[18, -8, -20]} color="#6b3d68" intensity={40} distance={50} decay={2} />
      <ambientLight intensity={0.16} />
    </>
  );
}
